import { socketMessageReceived, wsConnected, wsDisconnected } from "../websockets/actions"
import { WS_CONNECT, WS_DISCONNECT } from "../websockets/types"
import { receiveLiveUpdate } from "../live-updates/registry"
import { LIVE_UPDATES_SUBSCRIBE, LIVE_UPDATES_UNSUBSCRIBE } from "../live-updates/types"
import { getAuthHeaders, getSocketUrl } from "../websocket-utils"

const RECONNECT_BASE_DELAY_MS = 2000 // 2 seconds
const RECONNECT_MAX_DELAY_MS = 60000 // 60 seconds

const socketMiddleware = () => {
    // Connections are keyed by host so that features wanting different channels can hold them at
    // the same time. A single slot meant opening one channel silently evicted another — the TK
    // export side rail and the weekly view live on the same page and would fight over it.
    const sockets = {}
    // Reconnect bookkeeping is per host, so one channel backing off never resets another's state.
    const reconnectState = {}

    const stateFor = host => {
        if (!reconnectState[host]) reconnectState[host] = { retryCount: 0, reconnectTimer: null, isGuest: false }
        return reconnectState[host]
    }

    const clearReconnectTimer = host => {
        const state = reconnectState[host]
        if (state?.reconnectTimer !== null && state?.reconnectTimer !== undefined) {
            clearTimeout(state.reconnectTimer)
            state.reconnectTimer = null
        }
    }

    const closeSocket = host => {
        clearReconnectTimer(host)
        if (sockets[host]) sockets[host].close()
        delete sockets[host]
        delete reconnectState[host]
    }

    // Topics the client has asked for on the live-updates channel, ref-counted so the channel stays
    // open exactly as long as something needs it: multiple consumers (e.g. the Weekly View grid and
    // a modal it opens) can subscribe to the same topic, and only the last one to leave should close
    // it. Kept here rather than in redux because channel-layer group membership is per connection: a
    // reconnect leaves the socket open but subscribed to nothing, so whatever owns the socket has to
    // re-send them.
    const liveUpdateTopicRefCounts = new Map()

    const isLiveUpdates = host => host === getSocketUrl("live_updates")

    const sendLiveUpdateTopics = host => {
        const socket = sockets[host]
        if (!socket || socket.readyState !== WebSocket.OPEN || !liveUpdateTopicRefCounts.size) return
        socket.send(JSON.stringify({ type: "add.to.topics", topics: [...liveUpdateTopicRefCounts.keys()] }))
    }

    // Tells the server to drop just these topics' group membership, for when other topics still
    // hold the connection open. When nothing is subscribed to anything, closeSocket is used instead
    // -- there's no point telling a connection to leave a group it's about to be torn down from.
    const sendRemoveLiveUpdateTopics = (host, topics) => {
        const socket = sockets[host]
        if (!socket || socket.readyState !== WebSocket.OPEN) return
        socket.send(JSON.stringify({ type: "remove.from.topics", topics }))
    }

    const closeAllSockets = store => {
        const openHosts = Object.keys(sockets)
        openHosts.forEach(host => closeSocket(host))
        // Only announce it if a non-live-updates host was actually closed, so a disconnect with
        // nothing open (or only the silent live-updates channel open) doesn't clear unrelated
        // notification state.
        if (openHosts.some(host => !isLiveUpdates(host))) store.dispatch(wsDisconnected())
    }

    // Handlers below take the specific socket instance they were bound to and bail if it's no
    // longer the one tracked for the host. Without that guard, a stale event from a socket that
    // was already replaced (e.g. closeSocket followed by an immediate reconnect) could delete or
    // dispatch on behalf of the newer connection.
    const onOpen = (store, host, socket) => () => {
        if (sockets[host] !== socket) return
        stateFor(host).retryCount = 0
        if (isLiveUpdates(host)) {
            sendLiveUpdateTopics(host)
            return
        }
        store.dispatch(wsConnected(host, socket))
    }
    const onClose = (store, host, socket) => event => {
        if (sockets[host] !== socket) return
        // Reconnect on unclean closes (abrupt drop) or server-side transient codes:
        //   1001 Going Away, 1011 Internal Error, 1012 Service Restart,
        //   1013 Try Again Later, 1014 Bad Gateway.
        // Code 1000 is a normal intentional close — do not reconnect.
        if (!event.wasClean || [1001, 1011, 1012, 1013, 1014].includes(event.code)) {
            const state = stateFor(host)
            const delay = Math.min(RECONNECT_BASE_DELAY_MS * Math.pow(2, state.retryCount), RECONNECT_MAX_DELAY_MS)
            state.retryCount++
            state.reconnectTimer = setTimeout(() => connectSocket(host, store, state.isGuest, true), delay)
        } else {
            delete sockets[host]
            // Nothing to dispatch for the live-updates channel: it drives no UI of its own, and
            // routing its lifecycle through wsDisconnected would reset the notification slice.
            if (!isLiveUpdates(host)) store.dispatch(wsDisconnected(host))
        }
    }
    const onMessage = (store, host, socket) => event => {
        if (sockets[host] !== socket) return
        // Live updates are silent by design, so they bypass the notification reducer entirely --
        // its default case pops a toast for any message it does not recognize.
        if (isLiveUpdates(host)) {
            try {
                receiveLiveUpdate(JSON.parse(event.data), store.dispatch)
            } catch {
                // A malformed frame is not worth breaking the socket over.
            }
            return
        }
        store.dispatch(socketMessageReceived(event))
    }

    const connectSocket = (host, store, isGuest = false, isReconnect = false) => {
        const state = stateFor(host)
        if (!isReconnect) {
            state.retryCount = 0
            state.isGuest = isGuest
        }
        // if we already have a socket open for this host, we don't need another one
        const existing = sockets[host]
        if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
            return
        }
        const socket = new WebSocket(host, getAuthHeaders(isGuest))
        socket.onclose = onClose(store, host, socket)
        socket.onopen = onOpen(store, host, socket)
        socket.onmessage = onMessage(store, host, socket)
        socket.keepAlive = true
        sockets[host] = socket
    }
    // the middleware part of this function
    return store => next => action => {
        switch (action.type) {
            case WS_CONNECT:
                if (!action.host) {
                    // if we don't have a host to connect to, we can't do anything
                    break
                }
                connectSocket(action.host, store, !!action.isGuest)
                break
            case LIVE_UPDATES_SUBSCRIBE: {
                // Subscribing drives the connection, so the only feature-flag check lives in the
                // page that subscribes -- no independent flag read at app startup to get wrong.
                action.topics.forEach(topic => liveUpdateTopicRefCounts.set(topic, (liveUpdateTopicRefCounts.get(topic) ?? 0) + 1))
                const host = getSocketUrl("live_updates")
                if (!host) break
                connectSocket(host, store)
                sendLiveUpdateTopics(host)
                break
            }
            case LIVE_UPDATES_UNSUBSCRIBE: {
                const droppedTopics = []
                action.topics.forEach(topic => {
                    const count = liveUpdateTopicRefCounts.get(topic)
                    if (!count) return
                    if (count > 1) {
                        liveUpdateTopicRefCounts.set(topic, count - 1)
                    } else {
                        liveUpdateTopicRefCounts.delete(topic)
                        droppedTopics.push(topic)
                    }
                })
                const host = getSocketUrl("live_updates")
                if (!liveUpdateTopicRefCounts.size) {
                    // Nothing is subscribed to anything anymore -- close the channel so the server
                    // drops our channel-layer group membership instead of pushing events into the
                    // void for the rest of the session.
                    closeSocket(host)
                } else if (droppedTopics.length) {
                    // Other topics still hold the connection open, so only tell the server to leave
                    // the groups nothing wants anymore instead of tearing down the whole channel.
                    sendRemoveLiveUpdateTopics(host, droppedTopics)
                }
                break
            }
            case WS_DISCONNECT:
                // A host closes just that channel. Without one, close them all — callers that
                // predate keyed connections rely on that, and so does logging out (see LOGOUT below).
                // closeSocket drops its entry from `sockets` before the close event fires, so the
                // stale-instance guard in onClose swallows it. Announce the disconnect here instead,
                // otherwise the store keeps a reference to a closed socket and callers that check
                // for one before connecting never reconnect.
                if (action.host) {
                    closeSocket(action.host)
                    // Mirror onClose: the live-updates channel drives no UI of its own, so its
                    // disconnect must not reset the notification slice.
                    if (!isLiveUpdates(action.host)) store.dispatch(wsDisconnected(action.host))
                } else {
                    closeAllSockets(store)
                }
                break
            case "LOGOUT":
                // A cookie-backed session's logout clears auth and dispatches this without a
                // full-page navigation, so nothing else tears down an open socket -- left alone, the
                // server keeps the logged-out employee's channel-group membership and keeps
                // delivering to it. Fall through to the reducers below; they also key off LOGOUT.
                closeAllSockets(store)
                liveUpdateTopicRefCounts.clear()
                return next(action)
            default:
                // this is middleware, so we need to pass the action along if it isn't the one we want
                return next(action)
        }
    }
}
export default socketMiddleware()
